import * as Yup from "yup";
import getValidationErrors from "./getValidationError";

export type PropertyAlternativeContact = {
  contact_email: string;
  contact_phone: string;
};

export const PROPERTY_ALTERNATIVE_PHONE_REGEX = /^\(\d{2}\)\s\d{4,5}-\d{4}$/;

const LANDLINE_PHONE_MASK: (string | RegExp)[] = [
  "(",
  /\d/,
  /\d/,
  ")",
  " ",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  "-",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

const MOBILE_PHONE_MASK: (string | RegExp)[] = [
  "(",
  /\d/,
  /\d/,
  ")",
  " ",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  "-",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

/** Brazilian mobiles start with 9 after the area code (11 digits total). */
export function shouldUseMobilePhoneMask(digits: string): boolean {
  if (digits.length > 10) return true;
  return digits.length >= 3 && digits[2] === "9";
}

export function formatPropertyAlternativePhone(digits: string): string {
  const normalized = digits.replace(/\D/g, "");
  if (normalized.length === 0) return "";

  const area = normalized.slice(0, 2);
  const local = normalized.slice(2);

  if (shouldUseMobilePhoneMask(normalized)) {
    const mobile = local.slice(0, 9);
    if (mobile.length <= 5) {
      return `(${area}) ${mobile}`.trim();
    }
    return `(${area}) ${mobile.slice(0, 5)}-${mobile.slice(5)}`.trim();
  }

  const landline = local.slice(0, 8);
  if (landline.length <= 4) {
    return `(${area}) ${landline}`.trim();
  }
  return `(${area}) ${landline.slice(0, 4)}-${landline.slice(4)}`.trim();
}

export function buildPropertyAlternativePhoneMask(rawValue = ""): (string | RegExp)[] {
  const digits = rawValue.replace(/\D/g, "");
  return shouldUseMobilePhoneMask(digits) ? MOBILE_PHONE_MASK : LANDLINE_PHONE_MASK;
}

export function normalizePropertyAlternativeContactFromApi(raw: {
  contact_email?: string | null;
  contact_phone?: string | null;
}): PropertyAlternativeContact {
  return {
    contact_email: String(raw.contact_email ?? "").trim(),
    contact_phone: String(raw.contact_phone ?? "").trim(),
  };
}

export function applyPropertyAlternativeContactToPayload(
  payload: Record<string, unknown>,
  contact: PropertyAlternativeContact
): Record<string, unknown> {
  const email = contact.contact_email.trim();
  const phone = contact.contact_phone.trim();

  return {
    ...payload,
    contact_email: email === "" ? null : email,
    contact_phone: phone === "" ? null : phone,
  };
}

export function optionalAlternativeContactYupFields() {
  return {
    contact_email: Yup.string()
      .trim()
      .max(255, "E-mail deve ter no máximo 255 caracteres")
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .test(
        "email-format",
        "Informe um e-mail válido",
        (value) => !value || Yup.string().email().isValidSync(value)
      ),
    contact_phone: Yup.string()
      .trim()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .test(
        "phone-format",
        "Informe um telefone válido",
        (value) => !value || PROPERTY_ALTERNATIVE_PHONE_REGEX.test(value)
      ),
  };
}

export function validatePropertyAlternativeContact(
  contact: PropertyAlternativeContact
): Partial<Record<"contact_email" | "contact_phone", string>> {
  try {
    Yup.object()
      .shape(optionalAlternativeContactYupFields())
      .validateSync(contact, { abortEarly: false });
    return {};
  } catch (err) {
    if (err instanceof Yup.ValidationError) {
      return getValidationErrors(err) as Partial<
        Record<"contact_email" | "contact_phone", string>
      >;
    }
    return {};
  }
}

export const EXPLICIT_NULLABLE_PROPERTY_STRING_KEYS = new Set([
  "contact_email",
  "contact_phone",
  "housing_program",
]);
